import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { STATUS_TYPES, USER_ORDER_PAYMENT_STATUS } from "~/utils/premium/constants";
import { formatHttpError, MissingAuthTokenError } from "~/utils/apiError";
import type { ActiveOrderTransaction, OrderUserLedger } from "~/types/payments";

// --------------- Interfaces ---------------

export interface PremiumOrderConfig {
  date: string;
  time: string;
  orderId: string | null;
  // The maker the launch order already uses, captured from the SAME click that set `orderId`.
  // Only meaningful as a pair with it: an order id on its own cannot tell "re-curate this
  // order" from "swap it to a different maker", which is what let the curate screen cancel an
  // order and create a replacement when it should have updated the menu in place.
  makerId: string | number | null;
  isGroupOrder: boolean | null;
}

export interface OrganisationOrder {
  id: string | number;
  maker_id: string | number;
  maker?: Record<string, unknown>;
  status?: string;
  is_group_order?: boolean;
  is_scheduled?: boolean;
  delivery_date_time?: string;
  confirm_date_time?: string;
  cutoff_date_time?: string;
  package_type_id?: number;
  orderProducts?: unknown[];
  orderPackages?: unknown[];
  [key: string]: unknown;
}

// Default for `activeOrderTransaction` — used to seed state and reset
// it on enterPremiumStore / maker-switch / clearPointer flows.
const defaultActiveTransaction: ActiveOrderTransaction = {
  active: false,
  pay_ledger: null,
  pay_payment_intent: null,
  pay_client_secret: null,
  stripe_payment_intent_status: null,
  refund_ledger: null,
  refund_batch: null,
  has_unresolved_pay_ledger: false,
};

const defaultPremiumConfig: PremiumOrderConfig = {
  date: "",
  time: "",
  orderId: null,
  makerId: null,
  isGroupOrder: null,
};

export const usePremiumStore = defineStore("premium", () => {
  const authStore = useAuthStore();
  const ordersStore = useOrdersStore();
  const { authHeaders, hasToken, apiBase } = useAuthenticatedFetch();
  const { fetchAttachments } = useAttachments();

  // --------------- State ---------------
  const selectedPremiumOrderConfig = ref<PremiumOrderConfig>({
    ...defaultPremiumConfig,
  });
  const premiumOrder = ref<unknown | false>(false);
  const premiumOrdersLoading = ref(false);
  const premiumOrderCredit = ref<unknown | false>(false);
  // Menu-customisation orderability failures for the current group-order cart (live
  // feedback; the authoritative gate is the backend at checkout). Empty = all orderable.
  // Consumed by the store page: it drives the "unavailable dish" snackbar + the checkout
  // block (orderabilityBlocked). Rich per-line flagging UI remains chunk I / Phase 2.
  const orderabilityFailures = ref<
    Array<{ product_id: number; reason: string; modifier_ids?: number[] }>
  >([]);
  // Monotonic id for the in-flight orderability check. The cart watcher can fire several
  // checks in quick succession (+/- on a line), and they can resolve out of order — an
  // older, slower response would otherwise overwrite a newer one and either show a stale
  // "unavailable" flag or clear a live one. Only the newest request may write.
  let latestOrderabilityRequestId = 0;
  const organisationOrders = ref<OrganisationOrder[] | false>(false);
  const organisationOrderProducts = ref<unknown[] | false>(false);
  const previousOrganisationOrders = ref<OrganisationOrder[]>([]);
  // Unified payment + refund state from the active-transaction endpoint.
  // Drives every blocker in the UI via `userOrderPaymentStatus`.
  const activeOrderTransaction = ref<ActiveOrderTransaction>({
    ...defaultActiveTransaction,
  });
  const appendProductsLoading = ref(false);
  const appendProductsError = ref<string | false>(false);
  const updateProductsLoading = ref(false);
  const updateProductsError = ref<string | false>(false);

  // --------------- Actions ---------------

  function setPremiumOrderConfig(config: Partial<PremiumOrderConfig>) {
    selectedPremiumOrderConfig.value = {
      ...selectedPremiumOrderConfig.value,
      ...config,
    };
    if (import.meta.client) {
      sessionStorage.setItem(
        "selected-premium-order-config",
        JSON.stringify(selectedPremiumOrderConfig.value),
      );
    }
  }

  function clearPremiumOrderConfig() {
    selectedPremiumOrderConfig.value = { ...defaultPremiumConfig };
    if (import.meta.client) {
      sessionStorage.removeItem("selected-premium-order-config");
    }
  }

  // One-shot signal for /orders/upcoming: "a premium order for this date was
  // just genuinely confirmed/edited/paid, land on it". Distinct from
  // `selectedPremiumOrderConfig`, which stays populated for the whole time a
  // customer is inside the change-maker/premium-store flow (including if
  // they back out without completing anything) — that's the field those
  // pages need to know which order/day they're working on, but it's not a
  // safe signal for "did they just finish".
  const justCompletedEditDate = ref<string | null>(null);

  function setJustCompletedEditDate(date: string | null) {
    justCompletedEditDate.value = date;
    if (import.meta.client) {
      if (date) {
        sessionStorage.setItem("premium-just-completed-edit-date", date);
      } else {
        sessionStorage.removeItem("premium-just-completed-edit-date");
      }
    }
  }

  // Reads and clears the one-shot flag in a single step so a later, unrelated
  // visit to /orders/upcoming never re-consumes a stale date.
  function consumeJustCompletedEditDate(): string | null {
    const date =
      justCompletedEditDate.value ||
      (import.meta.client ? sessionStorage.getItem("premium-just-completed-edit-date") : null);
    setJustCompletedEditDate(null);
    return date;
  }

  function enterPremiumStore(config: Partial<PremiumOrderConfig>) {
    ordersStore.setCartType("premium");
    setPremiumOrderConfig(config);
    ordersStore.clearCart();
    // The pair of `leavePremiumStore`'s clear, and the one that matters when the page is
    // NOT unmounted between orders — switching maker or order re-enters here while the
    // route component is reused, so `onBeforeUnmount` never runs and the previous order's
    // verdict would survive into this one.
    clearOrderabilityFailures();
    // Reset transaction so stale data from a previous order doesn't disable inputs
    activeOrderTransaction.value = { ...defaultActiveTransaction };
  }

  function leavePremiumStore() {
    ordersStore.setCartType("default");
    ordersStore.clearCart({ clearLocal: false });
    // The verdict belongs to the order being left. Keeping it meant a drifted group order's
    // flags followed the customer onto the next order opened on this screen.
    clearOrderabilityFailures();
  }

  function updateOrganisationOrder(updatedOrder: OrganisationOrder) {
    if (!organisationOrders.value || !Array.isArray(organisationOrders.value)) return;
    const index = organisationOrders.value.findIndex((o) => o.id === updatedOrder.id);
    if (index !== -1) {
      const existing = organisationOrders.value[index];
      organisationOrders.value[index] = {
        ...updatedOrder,
        maker: existing.maker,
      };
    } else {
      organisationOrders.value.push(updatedOrder);
    }
  }

  function updateOrganisationOrderProperty(updates: {
    orderId: string | number;
    property: string;
    value: unknown;
  }) {
    if (!organisationOrders.value || !Array.isArray(organisationOrders.value)) return;
    const index = organisationOrders.value.findIndex((o) => o.id === updates.orderId);
    if (index !== -1) {
      (organisationOrders.value[index] as Record<string, unknown>)[updates.property] =
        updates.value;
    }
  }

  function removeOrganisationOrder(orderId: string | number) {
    if (Array.isArray(organisationOrders.value)) {
      organisationOrders.value = organisationOrders.value.filter((o) => o.id !== orderId);
    }
    if (Array.isArray(previousOrganisationOrders.value)) {
      previousOrganisationOrders.value = previousOrganisationOrders.value.filter(
        (o) => o.id !== orderId,
      );
    }
  }

  async function getOrganisationOrders(queryParams?: {
    filters?: {
      filters?: unknown[];
      date?: Record<string, unknown> & { operator?: string };
    };
    page?: number;
    limit?: number;
    orderBy?: string;
    orderDirection?: string;
  }) {
    if (!hasToken()) return;

    const filters = queryParams?.filters?.filters || [];
    const date = queryParams?.filters?.date;
    let filterQuery = "";
    if (Array.isArray(filters) && filters.length) {
      filterQuery +=
        "&filter=" +
        encodeURIComponent(
          JSON.stringify(
            filters.map((f: unknown) => {
              const arr = f as [string, unknown, boolean?, boolean?];
              return { key: arr[0], value: arr[1], exact: arr[2], not: arr[3] };
            }),
          ),
        );
    }
    if (date) {
      filterQuery += "&date=" + JSON.stringify(date);
    }

    const page = queryParams?.page || 1;
    const limit = queryParams?.limit || 999;
    let query = `?limit=${limit}&page=${page}${filterQuery}`;
    if (queryParams?.orderBy && queryParams?.orderDirection) {
      query += `&orderBy=${queryParams.orderBy}&orderDirection=${queryParams.orderDirection}`;
    }

    try {
      const response = await $fetch<{ orders: OrganisationOrder[] }>(
        `${apiBase}/api/customers/orders/searchOrdersByOrganisation${query}`,
        { headers: authHeaders() },
      );
      if (date?.operator === "BEFORE") {
        previousOrganisationOrders.value = response.orders;
      } else {
        organisationOrders.value = response.orders;
      }
    } catch {
      // Ensure we set empty arrays so isPageReady isn't blocked
      if (date?.operator === "BEFORE") {
        previousOrganisationOrders.value = [];
      } else {
        organisationOrders.value = [];
      }
    }
  }

  async function getOrganisationOrderProducts() {
    if (!hasToken()) return;

    try {
      const response = await $fetch<{ orderProducts: unknown[] }>(
        `${apiBase}/api/customers/orders/orderProducts`,
        { headers: authHeaders() },
      );
      organisationOrderProducts.value = response.orderProducts;
    } catch {
      // Error
    }
  }

  async function getOrganisationOrderProductsById(id: string) {
    if (!hasToken()) return;

    try {
      const response = await $fetch<unknown[]>(
        `${apiBase}/api/customers/orders/orderProducts/${id}`,
        { headers: authHeaders() },
      );
      organisationOrderProducts.value = response;
    } catch {
      // Error
    }
  }

  async function appendProductsToOrder(payload: { orderId: string; items: unknown[] }) {
    if (!hasToken()) return;

    appendProductsLoading.value = true;
    try {
      await $fetch(`${apiBase}/api/customers/orders/addToOrder/${payload.orderId}`, {
        method: "PUT",
        body: { items: payload.items },
        headers: authHeaders(),
      });

      const [ordersResponse, orderProductsResponse] = await Promise.all([
        $fetch<{ orders: OrganisationOrder[] }>(
          `${apiBase}/api/customers/orders/searchOrdersByOrganisation`,
          { headers: authHeaders() },
        ),
        $fetch<{ orderProducts: unknown[] }>(`${apiBase}/api/customers/orders/orderProducts`, {
          headers: authHeaders(),
        }),
      ]);

      organisationOrders.value = ordersResponse.orders;
      organisationOrderProducts.value = orderProductsResponse.orderProducts;
      appendProductsError.value = false;
      appendProductsLoading.value = false;
    } catch (error: unknown) {
      appendProductsLoading.value = false;
      const msg = error instanceof Error ? error.message : "";
      if (msg.includes("Not enough notice")) {
        appendProductsError.value =
          "The deadline to add your selection to the order has passed. If you think this is incorrect please reach out to support.";
      } else if (msg.includes("Product has already been added")) {
        appendProductsError.value =
          "You have already added your selection to this order. If you think this is incorrect please reach out to support.";
      } else {
        appendProductsError.value =
          "There was an error adding your selection to this order. If this error persists please reach out to support.";
      }
    }
  }

  async function createDailyOrder(payload: {
    cart: {
      items: unknown[];
      date: { chosenDate: { full: string }; selectedWindow: string };
      deal_id?: string | null;
    };
    packageQuantity: number;
    chosenPackageTypeId: number;
    orderNote?: string;
  }) {
    if (!hasToken()) return;

    const makersStore = useMakersStore();
    const orgStore = useOrganisationStore();


    const pkg = makersStore.chosenPackage as Record<string, unknown> | false
    const makerFromPackage = (pkg && (pkg.maker as Record<string, unknown>)) || null

    const items = (
      payload.cart.items as Array<{
        product: { id: string; name: string; price_gross: number };
        quantity: number;
        appliedModifiers: unknown[];
      }>
    ).map((item) => ({
      id: item.product.id,
      name: item.product.name,
      quantity: item.quantity,
      appliedModifiers: item.appliedModifiers,
      itemPrice: item.product.price_gross,
    }));

    const body = {
      packages: [
        {
          id: pkg.id,
          name: pkg.name,
          package: {
            ...pkg,
            quantity: payload.packageQuantity,
          },
          package_type_id: payload.chosenPackageTypeId,
          quantity: payload.packageQuantity,
        },
      ],
      maker_id: makerFromPackage ? makerFromPackage.id : undefined,
      customer_first_name: authStore.user?.first_name,
      customer_last_name: authStore.user?.last_name,
      customer_email: authStore.user?.email,
      customer_phone: authStore.user?.phone || orgStore.organisation?.telephone,
      business_name: orgStore.organisation?.name || authStore.user?.business_name,
      delivery_note: orgStore.organisation?.delivery_note,
      note: payload.orderNote,
      biz: true,
      delivery_address: orgStore.organisation?.delivery_address,
      pick_up_address: makerFromPackage ? makerFromPackage.address : undefined,
      billing_address: orgStore.organisation?.billing_address || orgStore.organisation?.delivery_address,
      delivery_date_time: `${payload.cart.date.chosenDate.full} ${payload.cart.date.selectedWindow}`,
      items,
      deal_id: payload.cart.deal_id || null,
    };

    return $fetch(`${apiBase}/api/customers/orders/daily`, {
      method: "POST",
      body,
      headers: authHeaders(),
    });
  }

  async function createPremiumOrder(
    payload: Record<string, unknown> & {
      chosenDateFull: string;
      time: string;
    },
  ) {
    if (!hasToken()) return;

    const deliveryDateTime = `${payload.chosenDateFull} ${payload.time}`;

    return $fetch(`${apiBase}/api/customers/orders/premium/createPremiumOrder`, {
      method: "POST",
      body: { ...payload, delivery_date_time: deliveryDateTime },
      headers: authHeaders(),
    });
  }

  async function confirmPremiumOrder(orderId: string) {
    if (!hasToken()) return;

    await $fetch(`${apiBase}/api/customers/orders/premium/movePremiumOrderToCreated/${orderId}`, {
      headers: authHeaders(),
    });
    updateOrganisationOrderProperty({
      orderId,
      property: "status",
      value: STATUS_TYPES.CREATED,
    });
  }

  async function calculatePremiumCredit(payload: Record<string, unknown>) {
    if (!hasToken()) return;

    const response = await $fetch(
      `${apiBase}/api/customers/orders/premium/calculatePremiumOrderProductsSum`,
      { method: "POST", body: payload, headers: authHeaders() },
    );
    premiumOrderCredit.value = response;
  }

  // Live orderability check for a group-order cart (called alongside the totals recalc).
  // Records the failing lines in `orderabilityFailures` so the store page can surface
  // them. Never throws / never blocks here — the authoritative gate is the backend at
  // Drop any drifted-line verdict and invalidate anything still in flight.
  //
  // The failures describe ONE order, but the store outlives the page: without this, a
  // drifted group order's flags followed the customer onto the next order they opened on
  // the same screen — greying out lines and blocking checkout on an order that was never
  // evaluated, with no control anywhere to clear them. Bumping the request id matters as
  // much as emptying the array: a response already in flight for the previous order would
  // otherwise land afterwards and write its failures straight back in.
  function clearOrderabilityFailures() {
    orderabilityFailures.value = [];
    latestOrderabilityRequestId += 1;
  }

  // initPremiumCheckout. Returns { orderable, failures }.
  async function checkOrderability(
    orderId: string | number,
    items: Array<{ product_id: number; appliedModifiers?: Array<{ modifier_id: number }> }>,
  ) {
    const emptyResult = { orderable: true, failures: [] as typeof orderabilityFailures.value };
    if (!hasToken()) {
      // No token (e.g. logged out / expired) — clear any prior failures so stale
      // warnings don't linger in the UI.
      orderabilityFailures.value = [];
      // Invalidate anything already in flight as well. Clearing the failures is not enough
      // on its own: without bumping the counter, an older request's `isStale()` is still
      // false, so it can land after the logout and write its failures straight back in.
      latestOrderabilityRequestId += 1;
      return emptyResult;
    }

    const requestId = ++latestOrderabilityRequestId;
    const isStale = () => requestId !== latestOrderabilityRequestId;

    try {
      const response = await $fetch<{
        orderable: boolean;
        failures: typeof orderabilityFailures.value;
      }>(`${apiBase}/api/customers/orders/premium/checkOrderability/${orderId}`, {
        method: "POST",
        body: { products: items },
        headers: authHeaders(),
      });
      // Normalise `failures` to an array on BOTH the stored ref and the returned value —
      // callers do `result.failures.length`, so a response that omits `failures` must not
      // throw (which, on the mount path, would eject the user off the store page).
      const failures = response.failures ?? [];
      // A newer check has been fired since this one left, so this answer describes a cart
      // that no longer exists. Report the empty result rather than the stale one: the
      // caller ACTS on the return value — `runOrderabilityCheck` snackbars from it and
      // overwrites `notifiedFailingIds` — so handing back a superseded payload told
      // customers who had just removed the offending line that it was unavailable.
      if (isStale()) return emptyResult;
      orderabilityFailures.value = failures;
      return { orderable: response.orderable, failures };
    } catch {
      // Feedback-only — a failed check must never block the customer; the checkout
      // gate enforces. Clear stale failures so the UI doesn't show a false positive.
      if (!isStale()) orderabilityFailures.value = [];
      return emptyResult;
    }
  }

  async function initPremiumCheckout(payload: { orderId: string; items: unknown[] }) {
    if (!hasToken()) return;

    ordersStore.setCartLoading(true);
    try {
      const response = await $fetch<unknown>(
        `${apiBase}/api/customers/orders/premium/initPremiumCheckout/${payload.orderId}`,
        {
          method: "POST",
          body: { products: payload.items },
          headers: authHeaders(),
        },
      );
      ordersStore.setCartLoading(false);
      return response;
    } catch (error: unknown) {
      ordersStore.setCartLoading(false);
      // If the checkout gate rejected on menu customisation, the backend returns 400 with
      // { orderable:false, failures:[...] }. Refresh the failures (so the checkout block +
      // inline prompt reflect it) and show a specific message instead of the generic one.
      const data = (error as { data?: { orderable?: boolean; failures?: unknown } })?.data;
      if (data && data.orderable === false && Array.isArray(data.failures)) {
        orderabilityFailures.value = data.failures as typeof orderabilityFailures.value;
        ordersStore.setCartError(
          "Some items in this cart are no longer available because they violate organisation rules. Remove them before checking out.",
        );
      } else {
        ordersStore.setCartError(extractErrorMessage(error));
      }
      throw new Error("Failed to submit order");
    }
  }

  async function updateOrderPackage(payload: {
    orderId: string;
    items: unknown[];
    packages: unknown[];
    note?: string | null;
    deliveryDateTime?: string;
  }) {
    if (!hasToken()) return;

    updateProductsLoading.value = true;
    updateProductsError.value = false;
    try {
      const response = await $fetch<OrganisationOrder>(
        `${apiBase}/api/customers/orders/updatePackage/${payload.orderId}`,
        {
          method: "PUT",
          body: {
            items: payload.items,
            packages: payload.packages,
            note: payload.note,
            ...(payload.deliveryDateTime
              ? { delivery_date_time: payload.deliveryDateTime }
              : {}),
          },
          headers: authHeaders(),
        },
      );
      updateOrganisationOrder(response);
      updateProductsLoading.value = false;
      updateProductsError.value = false;
    } catch (error) {
      updateProductsLoading.value = false;
      // A delivery date/time change re-books the courier in the background. When PedalMe can't
      // re-book, the backend reverts the date change and returns 409 { rebookFailed: true } —
      // surface that specific reason (the edit modal stays open, keeping the original date).
      const data = (error as { data?: { rebookFailed?: boolean; message?: string } })?.data;
      if (data?.rebookFailed) {
        updateProductsError.value =
          data.message ||
          "We couldn't re-book the courier for that delivery time, so the change wasn't saved. Please pick a different time or contact us.";
        return;
      }
      updateProductsError.value =
        "There was an error updating products for this order. If this error persists please reach out to support.";
    }
  }

  async function swapPremiumOrder(payload: {
    currentId: string;
    newOrder: Record<string, unknown> & {
      chosenDateFull: string;
      time: string;
    };
  }) {
    await cancelPremiumOrder(payload.currentId);
    await createPremiumOrder(payload.newOrder);
  }

  // THROWS when there is no token, rather than returning early like its neighbours.
  //
  // A silent skip here is not "nothing happened" — it is "the caller now believes an order was
  // cancelled when it is still live". The curate screen's swap arm awaits this, then clears the
  // launch pair and creates the replacement; with the early-return version a session that had
  // expired produced two live orders for the same date and never cancelled the first, because
  // the `catch` that exists for exactly this could not fire on a resolved promise (Darko,
  // PR #187). `change-maker.vue` awaits it the same way, before `createDailyOrder`.
  //
  // Deliberately NOT applied to `createPremiumOrder`, which keeps its `undefined` sentinel: its
  // return value is consumed (the caller needs the id) and the curate page already branches on
  // `=== undefined` with its own copy and tests. `createDailyOrder` and `swapPremiumOrder` still
  // return early and carry the same latent risk on change-maker — noted, not fixed here.
  async function cancelPremiumOrder(orderId: string) {
    if (!hasToken()) {
      throw new MissingAuthTokenError(
        "Your session has expired, so we couldn't release your current order.",
      );
    }

    await $fetch(`${apiBase}/api/customers/orders/premium/cancelPremiumOrder/${orderId}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
  }

  async function getValidSubscriptionMakers(params: Record<string, unknown>) {
    if (!hasToken()) return;

    const makersStore = useMakersStore();
    makersStore.setMakersLoading(true);

    const searchParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (Array.isArray(value)) {
        value.forEach((v) => searchParams.append(key, String(v)));
      } else {
        searchParams.append(key, String(value));
      }
    });

    try {
      const response = await $fetch<Array<Record<string, unknown>>>(
        `${apiBase}/api/customers/makers/filterMakersForSubscription?${searchParams.toString()}`,
        { headers: authHeaders() },
      );
      // List endpoint no longer embeds attachments — hydrate logo/profile in bulk.
      const makers = response ?? [];
      const byId = await fetchAttachments(
        "maker",
        makers.map((m) => m.id as string),
        ["logo", "profile"],
      );
      const hydrated = makers.map((m) => ({
        ...m,
        attachments: byId[m.id as string] ?? {},
      }));
      makersStore.setValidSubscriptionMakers(hydrated);
      makersStore.setMakersLoading(false);
    } catch {
      makersStore.setMakersLoading(false);
    }
  }

  /**
   * Fetch the unified active-transaction state for an order. Heavier endpoint
   * — includes a live Stripe PaymentIntent fetch — so use this when seeding a
   * payment UI on mount, not for tight polling. Tight polling should hit the
   * public `pay-ledger-status` endpoint via `usePayLedgerPolling`, or the
   * refund-batch poller in `useRefundBatchPolling`.
   *
   * The Stripe status returned alongside the transaction is also written
   * into the orders store so `userOrderPaymentStatus` can resolve correctly
   * on a cold page load (resume flow). Errors are NOT swallowed — callers
   * decide whether to recover or surface them.
   */
  async function fetchActiveOrderTransaction(orderId: string | number) {
    if (!hasToken()) return;

    const response = await $fetch<ActiveOrderTransaction>(
      `${apiBase}/api/customers/orders/payments/active-transaction/${orderId}`,
      { headers: authHeaders() },
    );
    activeOrderTransaction.value = response;
    if (response?.stripe_payment_intent_status) {
      ordersStore.setStripePaymentIntentStatus(response.stripe_payment_intent_status);
    }
    return response;
  }

  /**
   * New, clean signature — takes the Stripe PaymentIntent id directly (which
   * is the capability token) and hits the new payments-namespaced route. Use
   * this from new code; `invalidateOrderUserLedger(orderId)` below is a
   * compatibility shim that resolves the PI from current state first.
   */
  async function invalidatePayLedger(paymentIntentId: string) {
    if (!hasToken()) return;

    ordersStore.setCartLoading(true);
    try {
      await $fetch(
        `${apiBase}/api/customers/orders/payments/invalidate/${paymentIntentId}`,
        { method: "PUT", body: {}, headers: authHeaders() },
      );
      ordersStore.setCartLoading(false);
      // Old ledger is now invalidated — reset the transaction view so the UI
      // unblocks. Caller is expected to refetch / proceed with a fresh attempt.
      activeOrderTransaction.value = { ...defaultActiveTransaction };
    } catch (error: unknown) {
      ordersStore.setCartLoading(false);
      ordersStore.setCartError(formatHttpError(error));
      throw new Error("Failed to invalidate pay ledger", { cause: error });
    }
  }

  async function initPremiumRefund(orderId: string) {
    if (!hasToken()) return;

    return $fetch(`${apiBase}/api/customers/orders/premium/initPremiumRefund/${orderId}`, {
      method: "POST",
      body: {},
      headers: authHeaders(),
    });
  }

  async function cancelPremiumRefund(orderId: string) {
    if (!hasToken()) return;

    return $fetch(`${apiBase}/api/customers/orders/premium/cancelPremiumRefund/${orderId}`, {
      method: "PUT",
      body: {},
      headers: authHeaders(),
    });
  }

  /**
   * Retry an already-failed premium refund. Used by the FAILED branch of
   * the RefundOrder modal — gives the customer a one-click way to retry
   * the refund without re-opening it from scratch.
   */
  async function retryPremiumRefund(orderId: string) {
    if (!hasToken()) return;

    return $fetch(`${apiBase}/api/customers/orders/premium/retryPremiumRefund/${orderId}`, {
      method: "PUT",
      body: {},
      headers: authHeaders(),
    });
  }

  /**
   * New, clean signature — Stripe `payment_intent_id` is itself the capability.
   */
  async function cancelPayment(paymentIntentId: string) {
    if (!hasToken()) return;

    ordersStore.setCartLoading(true);
    try {
      await $fetch(
        `${apiBase}/api/customers/orders/payments/cancel/${paymentIntentId}`,
        { method: "PUT", body: {}, headers: authHeaders() },
      );
      ordersStore.setCartLoading(false);
      // The Stripe PI is now canceled — drop the in-flight transaction view.
      activeOrderTransaction.value = { ...defaultActiveTransaction };
    } catch (error: unknown) {
      ordersStore.setCartLoading(false);
      ordersStore.setCartError(formatHttpError(error));
      throw new Error("Failed to cancel payment", { cause: error });
    }
  }

  // ── Centralised payment status getter ──
  // Drives every UI blocker: FRESH → cart editable; PENDING / REFUND_PENDING
  // → disable inputs; AWAITING_PAYMENT_DETAILS → render Stripe Elements;
  // FAILED → offer retry / cancel-and-edit; COMPLETE → group-order lock.
  //
  // The pay-side branches are Stripe-status-driven (NOT
  // `confirmation_started_at` — that field is unused on PAY ledgers in the
  // new flow): a PENDING ledger is finalising while Stripe is processing /
  // succeeded, awaiting card details when Stripe needs payment_method or
  // confirmation, and failed when Stripe hits requires_action / canceled.
  // Falls back to the in-session `ordersStore.stripePaymentIntentStatus`
  // when the transaction response doesn't carry one (e.g. mid-checkout
  // before the next active-transaction refetch).
  const userOrderPaymentStatus = computed(() => {
    const tx = activeOrderTransaction.value;
    if (!tx.active) return USER_ORDER_PAYMENT_STATUS.FRESH;

    // A refund is only returned from the backend when its batch still has
    // entries awaiting confirmation (neither all_confirmed nor any_failed).
    // That state must block any further edits until the batch resolves.
    if (tx.refund_ledger) return USER_ORDER_PAYMENT_STATUS.REFUND_PENDING;

    const payLedger = tx.pay_ledger as OrderUserLedger | null;
    if (!payLedger) return USER_ORDER_PAYMENT_STATUS.FRESH;
    if (payLedger.invalidated_at !== null) return USER_ORDER_PAYMENT_STATUS.FRESH;

    // Non-group orders are partial-capable: a succeeded pay does not block
    // further edits. The cart is FRESH whenever no pay ledger is pending /
    // failed / requires_action. Group orders remain atomic and fall through.
    const isGroupOrder = !!selectedPremiumOrderConfig.value.isGroupOrder;
    if (!isGroupOrder && tx.has_unresolved_pay_ledger === false) {
      return USER_ORDER_PAYMENT_STATUS.FRESH;
    }

    if (payLedger.payment_status === "succeeded") return USER_ORDER_PAYMENT_STATUS.COMPLETE;

    if (payLedger.payment_status === "pending") {
      // Prefer the Stripe status returned alongside the transaction so
      // resume works on a cold page load. Fall back to the in-session
      // value set during an active checkout.
      const s = tx.stripe_payment_intent_status || ordersStore.stripePaymentIntentStatus;
      if (s === "processing" || s === "succeeded") return USER_ORDER_PAYMENT_STATUS.PENDING;
      if (s === "requires_action" || s === "canceled") return USER_ORDER_PAYMENT_STATUS.FAILED;
      if (s === "requires_payment_method" || s === "requires_confirmation")
        return USER_ORDER_PAYMENT_STATUS.AWAITING_PAYMENT_DETAILS;
      // Stripe retrieve failed or the status is unknown — default to the
      // safer "finalising your payment" path rather than prompting for
      // new card details.
      return USER_ORDER_PAYMENT_STATUS.PENDING;
    }

    if (
      payLedger.payment_status === "failed" ||
      payLedger.payment_status === "canceled" ||
      payLedger.payment_status === "requires_action"
    ) {
      return USER_ORDER_PAYMENT_STATUS.FAILED;
    }

    return USER_ORDER_PAYMENT_STATUS.PENDING;
  });

  return {
    // State
    selectedPremiumOrderConfig,
    premiumOrder,
    premiumOrdersLoading,
    premiumOrderCredit,
    orderabilityFailures,
    clearOrderabilityFailures,
    organisationOrders,
    organisationOrderProducts,
    previousOrganisationOrders,
    activeOrderTransaction,
    appendProductsLoading,
    appendProductsError,
    updateProductsLoading,
    updateProductsError,

    // Getters
    userOrderPaymentStatus,

    // Actions
    setPremiumOrderConfig,
    clearPremiumOrderConfig,
    justCompletedEditDate,
    setJustCompletedEditDate,
    consumeJustCompletedEditDate,
    enterPremiumStore,
    leavePremiumStore,
    updateOrganisationOrder,
    updateOrganisationOrderProperty,
    removeOrganisationOrder,
    getOrganisationOrders,
    getOrganisationOrderProducts,
    getOrganisationOrderProductsById,
    appendProductsToOrder,
    createDailyOrder,
    createPremiumOrder,
    confirmPremiumOrder,
    calculatePremiumCredit,
    checkOrderability,
    initPremiumCheckout,
    updateOrderPackage,
    swapPremiumOrder,
    cancelPremiumOrder,
    getValidSubscriptionMakers,
    fetchActiveOrderTransaction,
    cancelPayment,
    invalidatePayLedger,

    initPremiumRefund,
    cancelPremiumRefund,
    retryPremiumRefund,
  };
});
